import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";

/**
 * Header notification bell — surfaces critical composite-risk items.
 * Doctor/admin only. Polls /monitoring/notifications every 60s.
 */
export default function NotificationBell() {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/monitoring/notifications");
                if (cancelled) return;
                setItems(data.items || []);
                setCount(data.count || 0);
            } catch (e) {
                // Silently ignore — nurse/other roles may hit 403
                if (!cancelled) { setItems([]); setCount(0); }
            }
        };
        load();
        const id = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    return (
        <div className="relative" ref={ref} data-testid="notification-bell">
            <button
                onClick={() => setOpen((v) => !v)}
                className="relative p-2 rounded-full hover:bg-alt transition-colors"
                aria-label="Notificações"
                data-testid="notification-bell-btn"
            >
                <Bell size={18} className="text-brand" />
                {count > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#9E2A2B] text-white text-[10px] font-bold flex items-center justify-center"
                        data-testid="notification-badge"
                    >
                        {count > 9 ? "9+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-2xl border-2 border-[#EDE0C6] z-50"
                    data-testid="notification-panel"
                >
                    <div className="p-3 border-b border-[#EDE0C6] flex items-center justify-between bg-[#FBF7EF] rounded-t-xl">
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#8A6A15] font-semibold">Cuidado proativo</p>
                            <h4 className="font-display text-sm text-brand font-medium">Alertas críticos ({count})</h4>
                        </div>
                        <Link to="/monitoring" onClick={() => setOpen(false)} className="text-xs text-brand hover:underline">
                            Ver painel
                        </Link>
                    </div>
                    {items.length === 0 ? (
                        <p className="p-4 text-sm text-muted-clinical text-center">Nenhum alerta crítico ativo. Bom trabalho ✨</p>
                    ) : (
                        <ul className="divide-y divide-[#EDE0C6]">
                            {items.map((n, i) => (
                                <li key={`${n.patient_id}-${i}`}>
                                    <Link
                                        to={`/monitoring/patient/${n.patient_id}`}
                                        onClick={() => setOpen(false)}
                                        className="block p-3 hover:bg-[#FBF7EF] transition-colors"
                                        data-testid={`notification-item-${n.patient_id}`}
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="text-sm font-medium text-brand truncate">{n.patient_name}</span>
                                            <span className="text-[10px] uppercase tracking-widest text-[#9E2A2B] font-semibold shrink-0">crítico</span>
                                        </div>
                                        <span className="text-xs text-muted-clinical block mt-0.5">{n.label}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
